import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import PartDBService from '../../services/part-db-service';
import TklService from '../../services/tkl-service';
import { StyledDialogTitle } from '../StyledComponents';
import Loading from '../Loading';
import useErrorHandler from '../../services/error-handler';

class TklTypesStore {
    types = [];
    constructor() {
        makeAutoObservable(this);
    }
    async loading() {
        const response = await TklService.getTypes();
        this.types = response.data;
    }
}
const tklTypesStore = new TklTypesStore();

//-------------------------------------------------------------------------------------------------
const TypeSelector = observer(({ value, onChange }) => {
    const errorHandler = useErrorHandler();

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        try {
            await tklTypesStore.loading();
        } catch (e) {
            errorHandler(e);
        }
    };
    const renderTypeMenu = () => {
        return tklTypesStore.types.map((type) => (
            <MenuItem key={type.type_id} value={type.type_id}>
                {type.name}
            </MenuItem>
        ));
    };

    return (
        <FormControl margin="normal" size="small">
            <InputLabel id="type-selector" shrink={Boolean(value)}>
                Тип устройства
            </InputLabel>
            <Select
                label="Тип устройства"
                labelId="type-selector"
                variant="outlined"
                value={value}
                onChange={onChange}
                sx={{ minWidth: '10em' }}
            >
                {renderTypeMenu()}
            </Select>
        </FormControl>
    );
});

//=================================================================================================
const ErrorDialog = ({ data, onClose }) => {
    //data = {title, message}
    return (
        <Dialog open={data} onClose={onClose}>
            <StyledDialogTitle>{data.title}</StyledDialogTitle>
            <DialogContent>
                <Typography>{data.message}</Typography>
            </DialogContent>
            <DialogActions>
                <Button color="primary" variant="contained" onClick={onClose}>
                    Закрыть
                </Button>
            </DialogActions>
        </Dialog>
    );
};

//=================================================================================================
/*
    data = {
        current: 0,
        total: 100
    }
*/
const ProgressDialog = ({ data }) => {
    return (
        <Dialog open={data} disableRestoreFocus>
            <StyledDialogTitle>Выполняется добавление устройств...</StyledDialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                        <LinearProgress variant="determinate" value={data ? (100 * data.current) / data.total : 0} />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {`${Math.round(data ? (100 * data.current) / data.total : 0)}%`}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">
                        Выполняется {data?.current} из {data?.total}...
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

//=================================================================================================
const createTkl = (typeId, hwVersion) => {
    return {
        type_id: typeId,
        state_id: 1,
        hw_version: hwVersion,
        info: {},
    };
};

//=================================================================================================
/*
    data = {
        "name": "TKL-LogLW",
        "type_id": 13,
        "hw_version": "4.14",
        "project_name": "TKL-LogLW",
        "project_id": 6,
        "quantity": 1
    }
*/
const CorrectionDialog = ({ data, onApply, onClose }) => {
    const errorHandler = useErrorHandler();

    const [typeId, setTypeId] = useState('');
    const [hw, setHw] = useState('');
    const [progress, setProgress] = useState(null);

    useEffect(() => {
        if (data) init();
    }, [data]);

    const init = () => {
        setTypeId(data.type_id);
        setHw(data.hw_version);
    };

    const onApplyButtonClick = async () => {
        try {
            const tkl = createTkl(+typeId, hw);
            for (let i = 0; i < data.quantity; i++) {
                setProgress({ current: i + 1, total: data.quantity });
                await TklService.add(tkl);
            }
            onApply();
        } catch (e) {
            errorHandler(e);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') onApplyButtonClick();
    };

    const onTypeChanged = (e) => {
        setTypeId(+e.target.value);
    };

    if (!data) return null;
    if (progress) return <ProgressDialog data={progress} />;
    return (
        <Dialog open={Boolean(data)} onClose={onClose}>
            <StyledDialogTitle>Добавление устройств</StyledDialogTitle>
            <DialogContent>
                <Typography>
                    В PartDb были созданы {data?.project_name} в количестве {data?.quantity}.
                </Typography>
                <Typography>Добавить устройства в учет на portal?</Typography>
            </DialogContent>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Stack direction="row" spacing={2}>
                    <TypeSelector value={typeId} onChange={onTypeChanged} />
                    <TextField
                        label="Версия платы"
                        variant="outlined"
                        size="small"
                        value={hw}
                        onChange={(e) => setHw(e.target.value)}
                        onKeyDown={onKeyDown}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button color="success" onClick={onApplyButtonClick} variant="contained" autoFocus>
                    Добавить
                </Button>
                <Button onClick={onClose} variant="outlined">
                    Отмена
                </Button>
            </DialogActions>
        </Dialog>
    );
};

//=================================================================================================
/*
    data = {
        "project_id": 6,
        "quantity": 1
    }
*/
const InsertDialog = ({ data, onApply, onClose }) => {
    const errorHandler = useErrorHandler();

    const [projectName, setProjectName] = useState('');
    const [typeId, setTypeId] = useState('');
    const [hw, setHw] = useState('');
    const [progress, setProgress] = useState(null);

    useEffect(() => {
        if (data) init();
    }, [data]);

    const init = async () => {
        try {
            const response = await PartDBService.partProject(data.project_id); // {id, name, description}
            setProjectName(response.data.name ? response.data.name : '');
        } catch (e) {
            errorHandler(e);
        }
    };

    const onApplyButtonClick = async () => {
        try {
            if (!typeId) throw new Error('Тип устройства не выбран');
            const tkl = createTkl(+typeId, hw);
            for (let i = 0; i < data.quantity; i++) {
                setProgress({ current: i + 1, total: data.quantity });
                await TklService.add(tkl);
            }
            onApply();
        } catch (e) {
            errorHandler(e);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') onApplyButtonClick();
    };

    const onTypeChanged = (e) => {
        setTypeId(+e.target.value);
    };

    if (!data) return null;
    if (progress) return <ProgressDialog data={progress} />;
    return (
        <Dialog open={data} onClose={onClose}>
            <StyledDialogTitle>Добавление устройств</StyledDialogTitle>
            <DialogContent>
                <Typography>
                    В PartDb были созданы {projectName} в количестве {data?.quantity}.
                </Typography>
                <Typography>К сожалению нет информации о соответствующем типе устройства на portal.</Typography>
                <Typography>Для добавления устройств на portal выберете тип устройства:</Typography>
            </DialogContent>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Stack direction="row" spacing={2}>
                    <TypeSelector value={typeId} onChange={onTypeChanged} />
                    <TextField
                        label="Версия платы"
                        variant="outlined"
                        size="small"
                        value={hw}
                        onChange={(e) => setHw(e.target.value)}
                        onKeyDown={onKeyDown}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button color="success" onClick={onApplyButtonClick} variant="contained" autoFocus>
                    Добавить
                </Button>
                <Button onClick={onClose} variant="outlined">
                    Отмена
                </Button>
            </DialogActions>
        </Dialog>
    );
};

//=================================================================================================
const PartDbReceiving = () => {
    const navigate = useNavigate();
    const params = useParams();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [correctData, setCorrectData] = useState(null);
    const [insertData, setInsertData] = useState(null);

    useEffect(() => {
        init();
    }, []);

    const onErrorClose = () => {
        navigate('/');
    };

    const onApply = () => {
        navigate('/tkl', { state: { refresh: true } });
    };

    const init = async () => {
        const partDbId = +params.project_id || -1;
        const quantity = +params.quantity || -1;
        if (partDbId <= 0 || quantity <= 0) {
            setError({
                title: 'Ошибка',
                message: 'Неверные параметры запроса',
            });
            setLoading(false);
            return;
        }
        try {
            const response = await PartDBService.projectInfo(partDbId);
            // console.log('project >>>>', response.data);

            if (response.data) {
                if (response.data.type_id === 0) {
                    // Пропустить проект
                    window.close(); // Попытка закрыть вкладку, но если она была открыта не автоматически (пользователь перешел по ссылке), то она не закроется
                    const timer = setTimeout(() => onApply(), 200); // Если вкладка до сих пор не закрыта, редирект на главную
                    return () => clearTimeout(timer);
                } else {
                    setCorrectData({ ...response.data, quantity: quantity });
                    setLoading(false);
                }
            } else {
                setInsertData({ project_id: partDbId, quantity: quantity });
                setLoading(false);
            }
        } catch (e) {
            setError({ title: 'Ошибка', message: 'Ошибка получения данных с сервера' });
            setLoading(false);
        }
    };

    if (loading) return <Loading />;
    else if (error) return <ErrorDialog data={error} onClose={onErrorClose} />;
    else if (correctData) return <CorrectionDialog data={correctData} onClose={onErrorClose} onApply={onApply} />;
    else if (insertData) return <InsertDialog data={insertData} onClose={onErrorClose} onApply={onApply} />;
    else
        return (
            <ErrorDialog
                data={{ title: 'Не предвиденная ошибка', message: 'Что-то пошло не так, сообщите администратору...' }}
                onClose={onErrorClose}
            />
        );
};

export default PartDbReceiving;
